import type { AssistantConfig } from './assistant.ts'

/**
 * Where this app's own backend is, and the key to it.
 *
 * The address is a path on this same origin, not a host: `/api`. That is the
 * whole reason the page's security policy can stay at `connect-src 'self'` —
 * there is no other origin to allow, and the tablet has no permission to reach
 * anywhere else on the internet. Builds without a backend set it empty, and
 * then the assistant does not exist at all.
 *
 * The key is infrastructure, not family life, so it deliberately does not go
 * into the event log: the log is the record of what the children did, and it
 * gets exported and carried between devices. A credential has no business in it.
 */

const TOKEN_KEY = 'mh.assistant.token'

interface AssistantEnv {
  readonly VITE_API_URL?: string
}

const env = import.meta.env as AssistantEnv

export const ASSISTANT_URL = env.VITE_API_URL ?? ''

/** Whether this build has a backend behind it at all. */
export function assistantAvailable(): boolean {
  return ASSISTANT_URL.trim().length > 0
}

export function readToken(): string | undefined {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? undefined
  } catch {
    return undefined
  }
}

export function writeToken(token: string): void {
  try {
    if (token.trim().length === 0) localStorage.removeItem(TOKEN_KEY)
    else localStorage.setItem(TOKEN_KEY, token.trim())
  } catch {
    // A tablet with site data blocked simply has no key. The backend will
    // refuse the request and the screen will say so.
  }
}

export function assistantConfig(): AssistantConfig {
  return { url: ASSISTANT_URL, token: readToken() }
}
